import { useEffect, useState } from 'react';
import { ApiRequestError } from '../api/client';
import { api } from '../api/shop';
import type { VoucherShare } from '../api/types';
import { confirmDialog } from './Dialogs';

/**
 * Send yourself the QR code.
 *
 * ── The problem ─────────────────────────────────────────────────────────────
 *
 * Today the only way to show a voucher is to be signed in on the device that
 * bought it. That fails for somebody who bought on a laptop, and for the friend
 * collecting the order. So: a link, sent by SMS or WhatsApp, that opens a
 * full-screen QR and nothing else.
 *
 * ── Why this is not handing away money ──────────────────────────────────────
 *
 * The link carries the CODE. The PIN stays in this app, and the counter demands it
 * on every lookup — so a forwarded message is half a credential. The copy below
 * says so, because a customer who does not understand that will either refuse to
 * use the feature or use it carelessly, and both are worse than telling them.
 *
 * ── Why the URL is only in memory ───────────────────────────────────────────
 *
 * The server stores the token as a hash and returns the URL exactly once. So this
 * component holds it in state after creating it and cannot fetch it back. Pressing
 * share again mints a new link and revokes the old one, which is the right
 * behaviour anyway: the newest is the one being sent.
 */

export function ShareVoucher({ voucherId }: { voucherId: string }) {
  const [existing, setExisting] = useState<VoucherShare | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.voucherShare(voucherId).then(setExisting).catch(() => setExisting(null));
  }, [voucherId]);

  async function create() {
    setBusy(true);
    setError(null);

    try {
      const share = await api.createVoucherShare(voucherId);

      setUrl(share.url);
      setExisting(share);
    } catch (ex) {
      setError(
        ex instanceof ApiRequestError ? ex.message : 'Could not make a link just now.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!await confirmDialog(
      'Stop the link working?\n\nAnybody you already sent it to will see "this link no longer works".',
    )) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await api.revokeVoucherShare(voucherId);
      setExisting(null);
      setUrl(null);
    } catch {
      setError('Could not cancel that link.');
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (url === null) return;

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Refused in private windows and over plain HTTP. The link is on screen and
      // selectable, which is the fallback — not worth an error.
      setCopied(false);
    }
  }

  /**
   * The native share sheet where it exists, which is every modern phone. It offers
   * WhatsApp, Messages and everything else the person actually has installed —
   * better than guessing which two apps to link to.
   */
  async function nativeShare() {
    if (url === null) return;

    try {
      await navigator.share({
        title: 'My voucher',
        text: 'Here is my voucher — show this at the counter.',
        url,
      });
    } catch {
      // Cancelled, or unsupported. Neither is an error worth showing: the explicit
      // SMS and WhatsApp links below still work.
    }
  }

  const canNativeShare = typeof navigator !== 'undefined' && 'share' in navigator;

  return (
    <section className="share-block">
      <h2>Show this on another phone</h2>

      {url === null ? (
        <>
          <p className="muted">
            Send yourself a link that opens the QR code full screen — handy if you
            bought this on a computer, or if somebody else is collecting.
          </p>

          {existing !== null && (
            <p className="faint">
              You already have a link, valid until{' '}
              {new Date(existing.expiresAt).toLocaleDateString()}.
              {existing.openedCount > 0
                && ` It has been opened ${existing.openedCount} time${existing.openedCount === 1 ? '' : 's'}.`}
              {' '}Making a new one cancels it.
            </p>
          )}

          <div className="share-actions">
            <button type="button" className="btn" disabled={busy} onClick={() => void create()}>
              {busy ? 'Making a link…' : existing === null ? 'Create a link' : 'Create a new link'}
            </button>

            {existing !== null && (
              <button type="button" className="btn ghost" disabled={busy} onClick={() => void revoke()}>
                Cancel the link
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="muted">
            Send this to yourself. It works for 7 days, and you can cancel it any time.
          </p>

          <p className="share-url"><code>{url}</code></p>

          <div className="share-actions">
            {canNativeShare && (
              <button type="button" className="btn" onClick={() => void nativeShare()}>
                Share…
              </button>
            )}

            {/* Explicit fallbacks for a desktop browser, where there is no share
                sheet. `sms:` uses the ?&body= form, which is the spelling both iOS
                and Android accept. */}
            <a className="btn secondary" href={`sms:?&body=${encodeURIComponent(url)}`}>
              Send by SMS
            </a>

            <a
              className="btn secondary"
              href={`https://wa.me/?text=${encodeURIComponent(url)}`}
              target="_blank"
              rel="noreferrer"
            >
              WhatsApp
            </a>

            <button type="button" className="btn ghost" onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>

          {/* THE SENTENCE THAT MAKES THE FEATURE SAFE TO OFFER. A customer who
              understands that the link alone cannot spend anything will use it;
              one who does not will either avoid it or be careless with it. */}
          <p className="faint">
            The link shows the code but not your PIN, so it cannot be spent on its
            own — the counter will still ask you for your four digits.
          </p>
        </>
      )}

      {error !== null && <p className="notice error" role="alert">{error}</p>}
    </section>
  );
}
